import { useCallback, useEffect, useState } from 'react'
import { Button, Card, DatePicker, Descriptions, Divider, Form, Input, Modal, Select, Space, Table, TableProps, Tag } from 'antd'
import { DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, PrinterOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons'
import CustomTable from '../../components/table'
import defaultParams from '../../assets/constants/default_params'
import { useLoading } from '../../context/LoadingContext'
import { apiAddInvoiceAdapterByInvoiceId, apiDeleteInvoice, apiGetListCompanies, apiGetListInvoiceTemplateItems, apiGetListInvoiceTemplates, apiGetListInvoices, apiGetListTransactionConnectionByIds, apiGetListUsers, apiGetPrintInvoiceAdapter, apiUpdateInvoice } from '../../api'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { ICompany } from '../../models/company.modal'
import { IUser } from '../../models/user.model'
import { formatCurrency } from '../../utils/string-utils'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice'
import { renderProductNature } from '../kit-sim/order'
import { renderDateByUnix } from '../kit-sim/method'
import { toastError, toastSuccess } from '../../utils/toast'
import { saveAs } from "file-saver"
import { invoiceStatusOptions } from '../../assets/constants/default_options'
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc";
import moment from 'moment'
import * as XLSX from "xlsx"

dayjs.extend(utc);

export const renderInvoiceStatus = (state: string) => {
    const style: object = { width: 80, textAlign: 'center', marginRight: 0 }
    if (state === 'SUCCESS') return <Tag style={style} color="success">Đã xuất</Tag>
    if (state === 'FAIL') return <Tag style={style} color="error">Thất bại</Tag>
    if (state === 'ERROR') return <Tag style={style} color="error">Lỗi</Tag>
    if (state === 'PROCESSING') return <Tag style={style} color="processing">Đang xử lý</Tag>
    if (state === 'CREATED') return <Tag style={style} color="warning">Nháp</Tag>
    return
}

export default function InvoiceHistory() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [userOptions, setUserOptions] = useState<{ label: string, value: Number }[]>([])
    const [invoiceTemplates, setInvoiceTemplates] = useState<any>([])
    const [invoiceTemplateItems, setInvoiceTemplateItems] = useState<any>([])
    const [transactionConnection, setTransactionConnection] = useState<any>([])
    const [invoices, setInvoices] = useState<any>({})
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [search, setSearch] = useState<any>({ cpCode: '', owner_by: '', status: '' })
    const [modalDetailInvoiceTemplates, setModalDetailInvoiceTemplates] = useState<any>({ isOpen: false, data: null })
    const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false)
    const [modalInvoiceExport, setModalInvoiceExport] = useState<any>({ isOpen: false, data: null })
    const [invoiceExportValue, setInvoiceExportValue] = useState<any>({ customer_name: '', invoice_template_id: 0, cpCode: '', department: '', address: '', tax_code: '', identity_card: '' })
    const [showModalConfirmUpdate, setShowModalConfirmUpdate] = useState<boolean>(false)
    const [selectedRows, setSelectedRows] = useState<any>([])
    const [isOpenInvoiceSelected, setIsOpenInvoiceSelected] = useState<boolean>(false)
    const [modalDetailInvoiceSelected, setModalDetailInvoiceSelected] = useState<any>({ isOpen: false, data: null })
    const [modalDeleteInvoice, setModalDeleteInvoice] = useState<any>({ isOpen: false, data: null })
    const [isOpenDownloadExcel, setIsOpenDownloadExcel] = useState<boolean>(false)
    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()
    const { account } = useSelector(authSelector)

    const invoiceColumns: TableProps<any>['columns'] = [
        {
            key: 'cpCode',
            title: 'Đối tác',
            dataIndex: 'cpCode',
        },
        {
            key: 'owner_by',
            title: 'Nhân sự',
            dataIndex: 'owner_by',
        },
        {
            key: 'customer_name',
            title: 'Khách hàng',
            dataIndex: 'customer_name',
        },
        {
            key: 'phoneSim',
            title: 'Msisdn',
            render: data => transactionConnection.filter((config: any) => config.id === data?.tran_id)?.[0]?.phoneSim || ''
        },
        {
            key: 'tran_id',
            title: 'Serial Sim',
            render: data => transactionConnection.filter((config: any) => config.id === data?.tran_id)?.[0]?.serialSim || data?.tran_id
        },
        {
            key: 'total_amount',
            title: 'Tổng tiền',
            render: data => formatCurrency(invoiceTemplates.filter((config: any) => config.id === data?.invoice_template_id)?.[0]?.total_amount || 0)
        },
        {
            key: 'invoice_issued_date',
            title: 'Ngày xuất',
            dataIndex: 'invoice_issued_date',
            render: unixTime => renderDateByUnix(unixTime),
        },
        {
            key: 'invoice_id',
            title: 'Mã hoá đơn',
            dataIndex: 'invoice_id',
        },
        {
            key: 'status',
            title: 'Trạng thái',
            dataIndex: 'status',
            render: status => renderInvoiceStatus(status)
        },
        {
            key: 'created_at',
            title: 'Thời gian tạo',
            dataIndex: 'created_at',
            render: created_at => moment(created_at).format('DD/MM/YYYY HH:mm:ss'),
        },
        {
            key: 'action',
            title: 'Tác vụ',
            render: (_, data: any) => (
                <Button
                    onClick={() => {
                        setModalDetailInvoiceTemplates({ isOpen: true, data })
                    }}
                    style={{ background: '#9a29b6', color: 'white' }}
                    icon={<EyeOutlined />}
                />
            ),
        },
    ]

    const listInvoiceColumns: TableProps<any>['columns'] = [
        {
            key: 'owner_by',
            title: 'Nhân sự',
            dataIndex: 'owner_by',
        },
        {
            key: 'customer_name',
            title: 'Khách hàng',
            dataIndex: 'customer_name',
        },
        {
            key: 'total_amount',
            title: 'Tổng tiền',
            render: data => invoiceTemplates.filter((config: any) => config.id === data?.invoice_template_id).map((config: any, index: number) => (
                formatCurrency(config?.total_amount)
            ))
        },
        {
            key: 'invoice_issued_date',
            title: 'Ngày xuất',
            dataIndex: 'invoice_issued_date',
            render: unixTime => renderDateByUnix(unixTime),
        },
        {
            key: 'tran_id',
            title: 'Mã giao dịch',
            dataIndex: 'tran_id',
        },
        {
            key: 'action',
            title: 'Tác vụ',
            render: (_, data: any) => (
                <Button
                    onClick={() => {
                        setModalDetailInvoiceSelected({ isOpen: true, data })
                    }}
                    style={{ background: '#9a29b6', color: 'white' }}
                    icon={<EyeOutlined />}
                />
            ),
        },
    ]

    const invoiceTemplateItemsColumns: TableProps<any>['columns'] = [
        {
            key: 'product_line_number',
            title: 'STT',
            dataIndex: 'product_line_number',
        },
        {
            key: 'description',
            title: 'Tên hàng hoá',
            dataIndex: 'description',
        },
        {
            key: 'unit',
            title: 'Đơn vị',
            dataIndex: 'unit',
        },
        {
            key: 'quantity',
            title: 'Số lượng',
            dataIndex: 'quantity',
        },
        {
            key: 'unit_price',
            title: 'Đơn giá',
            dataIndex: 'unit_price',
            render: unit_price => formatCurrency(unit_price),
        },
        {
            key: 'product_nature',
            title: 'Loại sản phẩm',
            dataIndex: 'product_nature',
            render: product_nature => renderProductNature(product_nature),
        },
    ]

    const fetchCompanies = useCallback(async () => {
        try {
            const resp = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
            if (resp.data != null) {
                const options = resp.data.companies.map((item: ICompany) => ({
                    label: item.name,
                    value: item.code,
                }))
                setCompanyOptions(options)
            } else {
                setCompanyOptions([])
            }
        } catch (error) {
            console.log(error)
        }
    }, [])

    const fetchUsers = useCallback(async () => {
        try {
            const resp = await apiGetListUsers({ limit: defaultParams.MAX_LIMIT })
            if (resp.data != null) {
                const options = resp.data.users.map((item: IUser) => ({
                    label: item.username,
                    value: item.username,
                }))
                setUserOptions(options)
            } else {
                setUserOptions([])
            }
        } catch (error) {
            console.log(error)
        }
    }, [])

    const fetchInvoiceTemplates = useCallback(async () => {
        if (!account?.canGetListInvoiceTemplates) return
        try {
            const respInvoice = await apiGetListInvoiceTemplates()
            setInvoiceTemplates(respInvoice.data.templates)
        } catch (error) {
            console.log(error)
        }
    }, [])

    const fetchInvoiceTemplateItems = useCallback(async () => {
        if (!account?.canGetListInvoiceTemplateItems) return
        try {
            const respInvoice = await apiGetListInvoiceTemplateItems()
            setInvoiceTemplateItems(respInvoice.data.items)
        } catch (error) {
            console.log(error)
        }
    }, [])

    useEffect(() => {
        fetchCompanies()
        fetchUsers()
        fetchInvoiceTemplates()
        fetchInvoiceTemplateItems()
    }, [fetchCompanies, fetchUsers, fetchInvoiceTemplates, fetchInvoiceTemplateItems])

    const fetchInvoices = useCallback(async () => {
        if (!account?.canGetListInvoices || !search.cpCode) {
            setInvoices({})
            return
        }
        try {
            setIsLoading(true)
            let condition = `"cpCode"='${search.cpCode}'`
            if (search.owner_by) condition += ` AND owner_by='${search.owner_by}'`
            if (search.status) condition += ` AND status='${search.status}'`
            if (search.startDate > 0 && search.endDate > 0) {
                condition += ` AND (invoice_issued_date>=${search.startDate} AND invoice_issued_date<=${search.endDate})`
            }
            const respInvoice = await apiGetListInvoices({
                page: currentPage,
                size: pageSize,
                orderBy: 'created_at desc',
                condition: condition,
            })
            setInvoices(respInvoice.data)
            fetchListTransactionConnection(respInvoice.data?.invoices)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [search, currentPage, pageSize])

    const fetchListTransactionConnection = useCallback(async (invs: any) => {
        try {
            if (Number(invs?.length || 0) === 0) return
            const ids = invs?.map((inv: any) => inv.tran_id)
            const { data } = await apiGetListTransactionConnectionByIds({ ids })
            setTransactionConnection(data.transactions)
        } catch (error) {
            console.log(error)
        }
    }, [search.cpCode])

    useEffect(() => {
        fetchInvoices()
    }, [fetchInvoices])

    const handleSearch = ({ cpCode, owner_by, status, date }: any) => {
        if (!cpCode && !owner_by && !status) return
        let startDate, endDate
        if (date) {
            startDate = date[0] ? dayjs.utc(date[0]?.format('YYYY-MM-DD')).unix() : 0
            endDate = date[1] ? dayjs.utc(date[1]?.format('YYYY-MM-DD')).unix() : 0
        }
        setSearch({ cpCode, owner_by, status, startDate, endDate })
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setSearch({ cpCode: '', owner_by: '', status: '', startDate: 0, endDate: 0 })
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const handleInvoiceExport = async () => {
        try {
            setIsLoading(true)
            await apiAddInvoiceAdapterByInvoiceId({
                id: modalDetailInvoiceTemplates.data?.id,
            })
            fetchInvoices()
            setShowModalConfirm(false)
            setModalDetailInvoiceTemplates({ isOpen: false, data: null })
            toastSuccess('Xuất hóa đơn thành công!')
        } catch (error) {
            toastError('Có lỗi xảy ra!')
            console.log(error);
        } finally {
            setIsLoading(false)
        }
    }

    const handleInvoicePrint = async () => {
        try {
            setIsLoading(true)
            const network = invoiceTemplates.find((config: any) => config.id === modalDetailInvoiceTemplates.data?.invoice_template_id)?.network
            if (!network) {
                toastError('Không tìm thấy cấu hình hóa đơn!')
                return
            }
            const resp = await apiGetPrintInvoiceAdapter({
                id: modalDetailInvoiceTemplates.data?.invoice_id,
                network: network,
            })
            saveAs(resp.data, modalDetailInvoiceTemplates.data?.invoice_id + ".pdf");
            toastSuccess('Đã tải xuống!')
        } catch (error) {
            toastError('Có lỗi xảy ra!')
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleInvoiceUpdate = async () => {
        try {
            if (!invoiceExportValue.customer_name || !invoiceExportValue.invoice_template_id) {
                toastError('Vui lòng điền đầy đủ thông tin!')
                return
            }
            setIsLoading(true)
            await apiUpdateInvoice({
                invoice: {
                    id: modalDetailInvoiceTemplates.data?.id,
                    tran_id: modalDetailInvoiceTemplates.data?.tran_id,
                    customer_name: invoiceExportValue.customer_name?.trim().toUpperCase(),
                    invoice_template_id: invoiceExportValue.invoice_template_id,
                    cpCode: invoiceExportValue.cpCode,
                    department: invoiceExportValue.department,
                    address: invoiceExportValue.address,
                    tax_code: invoiceExportValue.tax_code,
                    identity_card: invoiceExportValue.identity_card,
                }
            })
            fetchInvoices()
            setShowModalConfirmUpdate(false)
            setModalDetailInvoiceTemplates({ isOpen: false, data: null })
            setModalInvoiceExport({ isOpen: false, data: null })
            setInvoiceExportValue({ customer_name: '', invoice_template_id: 0, cpCode: '', department: '', address: '', tax_code: '', identity_card: '' })
            toastSuccess('Cập nhật hóa đơn nháp thành công!')
        } catch (error) {
            toastError('Có lỗi xảy ra!')
            console.log(error);
        } finally {
            setIsLoading(false)
        }
    }

    const handleInvoiceSelected = async () => {
        try {
            setIsLoading(true)
            for (const item of selectedRows) {
                await apiAddInvoiceAdapterByInvoiceId({
                    id: item?.id,
                })
                const total_amount = invoiceTemplates
                    .filter((config: any) => config.id === item?.invoice_template_id)
                    .map((config: any) => formatCurrency(config?.total_amount))
                toastSuccess(`Xuất hóa đơn ${total_amount} cho ${item?.customer_name} thành công!`)
            }
            fetchInvoices()
            setSelectedRows([])
            setIsOpenInvoiceSelected(false)
            toastSuccess('Đã xuất hoá đơn thành công!')
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra, vui lòng thử lại sau')
        } finally {
            setIsLoading(false)
        }
    }

    const handleDeleteInvoice = async () => {
        try {
            setIsLoading(true)
            await apiDeleteInvoice({ invoice: modalDeleteInvoice.data })
            setModalDeleteInvoice({ isOpen: false, data: null })
            setModalDetailInvoiceTemplates({ isOpen: false, data: null })
            fetchInvoices()
            toastSuccess('Xoá hóa đơn thành công!')
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleDownloadExcel = async () => {
        try {
            setIsLoading(true)
            const dataExcel = invoices.invoices?.map((item: any) => ({
                'Đối tác': item.cpCode,
                'Nhân sự': item.owner_by,
                'Khách hàng': item.customer_name,
                'Msisdn': transactionConnection.filter((config: any) => config.id === item.tran_id)?.[0]?.phoneSim,
                'Serial Sim': transactionConnection.filter((config: any) => config.id === item.tran_id)?.[0]?.serialSim,
                'Tổng tiền': invoiceTemplates.filter((config: any) => config.id === item?.invoice_template_id)
                    .map((config: any) => formatCurrency(config?.total_amount))?.[0],
                'Ngày xuất': dayjs(item.invoice_issued_date * 1000).format('YYYY-MM-DD'),
                'Mã hóa đơn': item.invoice_id,
                'Trạng thái': invoiceStatusOptions.filter(i => i.value === item.status)?.[0]?.label,
                'Ngày tạo': moment(item.created_at).format('DD/MM/YYYY HH:mm:ss'),
            }))
            const ws = XLSX.utils.json_to_sheet(dataExcel)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, "Sheet1")
            const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" })
            const blob = new Blob([excelBuffer], { type: "application/octet-stream" })
            saveAs(blob, `${search.cpCode}.xlsx`)
            toastSuccess('Đã tải xuống!')
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <Form form={formSearch} onFinish={handleSearch}>
                <div style={{ display: 'flex', alignItems: 'start', flexWrap: 'wrap', gap: 20 }}>
                    <Form.Item name='cpCode' label='Công ty'
                        rules={[{ required: true, message: 'Vui lòng chọn công ty' }]}>
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn công ty"
                            options={companyOptions}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name='owner_by' label='Nhân sự đấu nối'>
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn nhân sự"
                            options={userOptions}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name='status' label='Trạng thái'>
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn trạng thái"
                            options={invoiceStatusOptions}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name="date" label="Ngày đấu nối">
                        <DatePicker.RangePicker
                            format="YYYY-MM-DD"
                            picker="date" />
                    </Form.Item>
                    <Space style={{ marginBottom: 24 }}>
                        <Button type='primary' htmlType="submit" icon={<SearchOutlined />}>Tìm kiếm</Button>
                        <Button onClick={handleClearSearch} icon={<UndoOutlined />}></Button>
                    </Space>
                </div>
            </Form>
            <CustomTable
                namePage='hoá đơn'
                columns={invoiceColumns}
                dataSource={invoices.invoices}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={invoices.total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                rowKey={(record: any) => record.id}
                rowSelection={{
                    selectedRowKeys: selectedRows.map((item: any) => item.id),
                    onChange: (_: any, selectedRows: any) => {
                        setSelectedRows(selectedRows)
                    },
                    getCheckboxProps: (record: any) => ({
                        disabled: record.status === "SUCCESS",
                    }),
                }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <Button
                    disabled={Number(invoices.invoices?.length || 0) === 0}
                    onClick={() => setIsOpenDownloadExcel(true)}
                >
                    Xuất excel ({invoices.invoices?.length || 0})
                </Button>
                <Button
                    disabled={selectedRows.length === 0}
                    onClick={() => setIsOpenInvoiceSelected(true)}>
                    Hoá đơn đã chọn ({selectedRows.length})
                </Button>
            </div>
            <Modal
                width={'64%'}
                title='Thông tin hoá đơn' open={modalDetailInvoiceTemplates.isOpen}
                onCancel={() => setModalDetailInvoiceTemplates({ isOpen: false, data: null })}
                footer={null}>
                <Divider />
                <Descriptions
                    items={[
                        {
                            key: 'phoneSim',
                            label: 'Msisdn',
                            children: transactionConnection.filter((config: any) => config.id === modalDetailInvoiceTemplates.data?.tran_id)?.[0]?.phoneSim,
                        },
                        {
                            key: 'serialSim',
                            label: 'Serial Sim',
                            children: transactionConnection.filter((config: any) => config.id === modalDetailInvoiceTemplates.data?.tran_id)?.[0]?.serialSim,
                        },
                    ]}
                />
                <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b>Khách hàng:</b>
                    <span>{modalDetailInvoiceTemplates.data?.customer_name}</span>
                </p>
                <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b>Đơn vị:</b>
                    <span>{modalDetailInvoiceTemplates.data?.department}</span>
                </p>
                <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b>Địa chỉ:</b>
                    <span>{modalDetailInvoiceTemplates.data?.address}</span>
                </p>
                <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b>Mã số thuế:</b>
                    <span>{modalDetailInvoiceTemplates.data?.tax_code}</span>
                </p>
                <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b>CCCD:</b>
                    <span>{modalDetailInvoiceTemplates.data?.identity_card}</span>
                </p>
                <Card size='small' style={{ marginTop: 10 }}>
                    {invoiceTemplates.filter((config: any) => config.id === modalDetailInvoiceTemplates.data?.invoice_template_id).map((config: any, index: number) => (
                        <div key={index}>
                            <Descriptions
                                items={[
                                    {
                                        key: 'cpCode',
                                        label: 'Đối tác',
                                        children: config?.cpCode,
                                    },
                                    {
                                        key: 'network',
                                        label: 'Network',
                                        children: config?.network,
                                    },
                                    {
                                        key: 'invoice_series',
                                        label: 'Mẫu số',
                                        children: config?.invoice_series,
                                    },
                                    {
                                        key: 'tran_amount',
                                        label: 'Số tiền giao dịch',
                                        children: formatCurrency(config?.tran_amount),
                                    },
                                    {
                                        key: 'total_amount',
                                        label: 'Tổng số tiền',
                                        children: formatCurrency(config?.total_amount),
                                    },
                                    {
                                        key: 'tax_rate',
                                        label: 'Thuế suất',
                                        children: config?.tax_rate + '%',
                                    },
                                    {
                                        key: 'currency_code',
                                        label: 'Loại tiền tệ',
                                        children: config?.currency_code,
                                    },
                                    {
                                        key: 'exchange_rate',
                                        label: 'Tỷ giá',
                                        children: config?.exchange_rate,
                                    },
                                    {
                                        key: 'payment_method_name',
                                        label: 'Hình thức thanh toán',
                                        children: config?.payment_method_name,
                                    },
                                    {
                                        key: 'alias',
                                        label: 'Tên template',
                                        children: config?.alias,
                                    },
                                ]}
                            />
                            <CustomTable
                                namePage='sản phẩm'
                                columns={invoiceTemplateItemsColumns}
                                dataSource={invoiceTemplateItems?.filter((i: any) => i.invoice_template_id === config?.id)}
                                total={invoiceTemplateItems?.filter((i: any) => i.invoice_template_id === config?.id)?.length}
                                isHiddenPagination={true}
                            />
                        </div>
                    ))}
                </Card>
                {(modalDetailInvoiceTemplates.data?.status === 'SUCCESS' || modalDetailInvoiceTemplates.data?.status === 'CREATED') && (
                    <div style={{ textAlign: 'right', marginTop: 20 }}>
                        {modalDetailInvoiceTemplates.data?.status === 'SUCCESS' ? (
                            invoiceTemplates.find((config: any) => config.id === modalDetailInvoiceTemplates.data?.invoice_template_id)?.network === 'OTEK-MISA-QUICK' ? <></> :
                                <Button disabled={!account?.canGetPrintInvoiceAdapter} type='primary' onClick={handleInvoicePrint} icon={<DownloadOutlined />}>Tải xuống</Button>
                        ) : (
                            <Space>
                                <Button disabled={!account?.canAddInvoiceAdapterByInvoiceId} type='primary' onClick={() => setShowModalConfirm(true)} icon={<PrinterOutlined />}>Xuất</Button>
                                <Button disabled={!account?.canUpdateInvoice} onClick={() => {
                                    setModalInvoiceExport({ isOpen: true, data: modalDetailInvoiceTemplates.data })
                                    setInvoiceExportValue({
                                        customer_name: modalDetailInvoiceTemplates.data?.customer_name,
                                        invoice_template_id: modalDetailInvoiceTemplates.data?.invoice_template_id,
                                        cpCode: modalDetailInvoiceTemplates.data?.cpCode,
                                        department: modalDetailInvoiceTemplates.data?.department,
                                        address: modalDetailInvoiceTemplates.data?.address,
                                        tax_code: modalDetailInvoiceTemplates.data?.tax_code,
                                        identity_card: modalDetailInvoiceTemplates.data?.identity_card,
                                    })
                                }} icon={<EditOutlined />}>Sửa</Button>
                                <Button danger disabled={!account?.canDeleteInvoice} onClick={() => {
                                    setModalDeleteInvoice({ isOpen: true, data: modalDetailInvoiceTemplates.data })
                                }} icon={<DeleteOutlined />}>Xoá</Button>
                            </Space>
                        )}
                    </div>
                )}
            </Modal>
            <Modal
                title='Xác nhận xuất hoá đơn' open={showModalConfirm}
                onCancel={() => setShowModalConfirm(false)}
                footer={[
                    <Button onClick={handleInvoiceExport}
                        style={{ background: '#008965', color: 'white' }}>
                        Xác nhận
                    </Button>,
                    <Button onClick={() => setShowModalConfirm(false)}
                        style={{ background: 'gray', color: 'white' }}>
                        Đóng
                    </Button>,
                ]}>
                <Divider />
                {invoiceTemplates.filter((config: any) => config.id === modalDetailInvoiceTemplates.data?.invoice_template_id).map((config: any, index: number) => (
                    <p key={index}>Xuất hoá đơn <b>{config?.alias}</b> cho khách hàng <b>{modalDetailInvoiceTemplates.data?.customer_name}</b></p>
                ))}
                <Divider />
            </Modal>
            <Modal
                title='Cập nhật hóa đơn'
                open={modalInvoiceExport.isOpen}
                footer={null}
                onCancel={() => setModalInvoiceExport({ isOpen: false, data: null })}>
                <Divider />
                <div>
                    <div style={{ marginBottom: 10 }}><span style={{ color: 'red', marginRight: 10 }}>*</span>Họ và tên KH</div>
                    <Input
                        value={invoiceExportValue.customer_name}
                        onChange={e => setInvoiceExportValue({ ...invoiceExportValue, customer_name: e.target.value })}
                    />
                    <div style={{ marginTop: 10 }}>
                        <div style={{ marginBottom: 10 }}><span style={{ color: 'red', marginRight: 10 }}>*</span>Chọn loại hoá đơn</div>
                        <Select
                            style={{ width: '100%' }}
                            value={invoiceExportValue.invoice_template_id}
                            onChange={value => setInvoiceExportValue({ ...invoiceExportValue, invoice_template_id: value })}
                            options={invoiceTemplates.filter((config: any) => config.cpCode === modalInvoiceExport.data?.cpCode).map((config: any) => ({
                                label: config.alias,
                                value: config.id,
                            }))}
                        />
                    </div>
                    {account?.canAddInvoiceByForm && (<>
                        <div style={{ marginBottom: 10, marginTop: 20 }}><span style={{ color: 'red', marginRight: 10 }}>*</span>Đối tác</div>
                        <Select
                            style={{ width: '100%' }}
                            showSearch
                            placeholder="Chọn công ty"
                            options={companyOptions}
                            filterOption={filterOptionByLabel}
                            value={invoiceExportValue.cpCode}
                            onChange={value => setInvoiceExportValue({ ...invoiceExportValue, cpCode: value })}
                        />
                        <div style={{ marginBottom: 10, marginTop: 20 }}>Đơn vị</div>
                        <Input
                            value={invoiceExportValue.department}
                            onChange={e => setInvoiceExportValue({ ...invoiceExportValue, department: e.target.value })}
                        />
                        <div style={{ marginBottom: 10, marginTop: 20 }}>Địa chỉ</div>
                        <Input
                            value={invoiceExportValue.address}
                            onChange={e => setInvoiceExportValue({ ...invoiceExportValue, address: e.target.value })}
                        />
                        <div style={{ marginBottom: 10, marginTop: 20 }}>Mã số thuế</div>
                        <Input
                            value={invoiceExportValue.tax_code}
                            onChange={e => setInvoiceExportValue({ ...invoiceExportValue, tax_code: e.target.value })}
                        />
                        <div style={{ marginBottom: 10, marginTop: 20 }}>CCCD</div>
                        <Input
                            value={invoiceExportValue.identity_card}
                            onChange={e => setInvoiceExportValue({ ...invoiceExportValue, identity_card: e.target.value })}
                        />
                    </>)}
                </div>
                <Divider />
                <div style={{ textAlign: 'right' }}>
                    <Button type='primary' onClick={() => setShowModalConfirmUpdate(true)}>Cập nhật</Button>
                </div>
            </Modal>
            <Modal
                title='Xác nhận cập nhật hoá đơn' open={showModalConfirmUpdate}
                onCancel={() => setShowModalConfirmUpdate(false)}
                footer={[
                    <Button onClick={handleInvoiceUpdate}
                        style={{ background: '#008965', color: 'white' }}>
                        Xác nhận
                    </Button>,
                    <Button onClick={() => setShowModalConfirmUpdate(false)}
                        style={{ background: 'gray', color: 'white' }}>
                        Đóng
                    </Button>,
                ]}>
                <Divider />
                {invoiceTemplates.filter((config: any) => config.id === invoiceExportValue?.invoice_template_id).map((config: any, index: number) => (
                    <p key={index}>Cập nhật hoá đơn <b>{config?.alias}</b> cho khách hàng <b>{invoiceExportValue?.customer_name}</b></p>
                ))}
                <Divider />
            </Modal>
            <Modal width={'64%'} title='Danh sách hoá đơn nháp cần xuất' open={isOpenInvoiceSelected}
                onCancel={() => setIsOpenInvoiceSelected(false)}
                footer={[
                    <Button onClick={handleInvoiceSelected} type='primary'>
                        Xuất
                    </Button>,
                ]}>
                <Divider />
                <div style={{ marginBottom: 20 }}>
                    <Table
                        dataSource={selectedRows}
                        columns={listInvoiceColumns}
                        pagination={false}
                    />
                </div>
            </Modal>
            <Modal
                width={'64%'}
                title='Thông tin hoá đơn' open={modalDetailInvoiceSelected.isOpen}
                onCancel={() => setModalDetailInvoiceSelected({ isOpen: false, data: null })}
                footer={null}>
                <Divider />
                <Descriptions
                    items={[
                        {
                            key: 'phoneSim',
                            label: 'Msisdn',
                            children: transactionConnection.filter((config: any) => config.id === modalDetailInvoiceSelected.data?.tran_id)?.[0]?.phoneSim,
                        },
                        {
                            key: 'serialSim',
                            label: 'Serial Sim',
                            children: transactionConnection.filter((config: any) => config.id === modalDetailInvoiceSelected.data?.tran_id)?.[0]?.serialSim,
                        },
                    ]}
                />
                <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b>Khách hàng:</b>
                    <span>{modalDetailInvoiceSelected.data?.customer_name}</span>
                </p>
                <Card size='small' style={{ marginTop: 10 }}>
                    {invoiceTemplates.filter((config: any) => config.id === modalDetailInvoiceSelected.data?.invoice_template_id).map((config: any, index: number) => (
                        <div key={index}>
                            <Descriptions
                                items={[
                                    {
                                        key: 'cpCode',
                                        label: 'Đối tác',
                                        children: config?.cpCode,
                                    },
                                    {
                                        key: 'network',
                                        label: 'Network',
                                        children: config?.network,
                                    },
                                    {
                                        key: 'invoice_series',
                                        label: 'Mẫu số',
                                        children: config?.invoice_series,
                                    },
                                    {
                                        key: 'tran_amount',
                                        label: 'Số tiền giao dịch',
                                        children: formatCurrency(config?.tran_amount),
                                    },
                                    {
                                        key: 'total_amount',
                                        label: 'Tổng số tiền',
                                        children: formatCurrency(config?.total_amount),
                                    },
                                    {
                                        key: 'tax_rate',
                                        label: 'Thuế suất',
                                        children: config?.tax_rate + '%',
                                    },
                                    {
                                        key: 'currency_code',
                                        label: 'Loại tiền tệ',
                                        children: config?.currency_code,
                                    },
                                    {
                                        key: 'exchange_rate',
                                        label: 'Tỷ giá',
                                        children: config?.exchange_rate,
                                    },
                                    {
                                        key: 'payment_method_name',
                                        label: 'Hình thức thanh toán',
                                        children: config?.payment_method_name,
                                    },
                                    {
                                        key: 'alias',
                                        label: 'Tên template',
                                        children: config?.alias,
                                    },
                                ]}
                            />
                            <CustomTable
                                namePage='sản phẩm'
                                columns={invoiceTemplateItemsColumns}
                                dataSource={invoiceTemplateItems?.filter((i: any) => i.invoice_template_id === config?.id)}
                                total={invoiceTemplateItems?.filter((i: any) => i.invoice_template_id === config?.id)?.length}
                                isHiddenPagination={true}
                            />
                        </div>
                    ))}
                </Card>
            </Modal>
            <Modal
                title='Xoá hoá đơn'
                open={modalDeleteInvoice.isOpen}
                footer={null}
                onCancel={() => setModalDeleteInvoice({ isOpen: false, data: null })}>
                <Divider />
                {invoiceTemplates.filter((config: any) => config.id === modalDeleteInvoice.data?.invoice_template_id).map((config: any, index: number) => (
                    <p key={index}>Bạn muốn xoá hoá đơn <b>{config?.alias}</b> cho khách hàng <b>{modalDeleteInvoice.data?.customer_name}</b></p>
                ))}
                <div style={{ textAlign: 'right' }}>
                    <Button type='primary' danger onClick={handleDeleteInvoice}>Xoá</Button>
                </div>
            </Modal>
            <Modal
                title='Tải xuống excel'
                open={isOpenDownloadExcel}
                footer={null}
                onCancel={() => setIsOpenDownloadExcel(false)}>
                <Divider />
                Xuất excel tổng cộng {invoices.invoices?.length} hoá đơn
                <div style={{ textAlign: 'right' }}>
                    <Button type='primary' onClick={handleDownloadExcel}>Tải xuống</Button>
                </div>
            </Modal>
        </div>
    )
}
